/**
 * UIHelpers — Shared UI utilities for tooltip, hover, and panel patterns.
 * Complements TechUI.ts (which handles buttons, chamfered rects, metal plates).
 *
 * Usage:
 *   import { createSimpleTooltip, addHoverColor, addHoverTint } from '@/ui/UIHelpers';
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';

// ── Tooltip ──────────────────────────────────────────────────────────────

export interface TooltipStyle {
  fontSize?: string;
  fontColor?: string;
  bgColor?: number;
  bgAlpha?: number;
  strokeColor?: number;
  strokeAlpha?: number;
  padding?: number;
  maxWidth?: number;
  depth?: number;
  borderRadius?: number;
  lineSpacing?: number;
}

const DEFAULT_TOOLTIP_STYLE: Required<TooltipStyle> = {
  fontSize: '10px',
  fontColor: '#ddeeff',
  bgColor: 0x111133,
  bgAlpha: 0.95,
  strokeColor: 0x6688aa,
  strokeAlpha: 0.8,
  padding: 8,
  maxWidth: 220,
  depth: 500,
  borderRadius: 6,
  lineSpacing: 3,
};

export interface SimpleTooltipHandle {
  /** Show the tooltip at the given world position with the given text. */
  show(worldX: number, worldY: number, text: string): void;
  /** Hide and destroy the current tooltip container. */
  hide(): void;
  /** The current container (null when hidden). */
  container: Phaser.GameObjects.Container | null;
}

/**
 * Creates a simple text tooltip that destroys/recreates on each show().
 * Used by scenes that need a single-text tooltip with background and screen clamping.
 *
 * The tooltip is positioned to the left of the cursor by default,
 * falling back to the right if there is not enough space.
 */
export function createSimpleTooltip(
  scene: Phaser.Scene,
  style?: TooltipStyle,
): SimpleTooltipHandle {
  const s = { ...DEFAULT_TOOLTIP_STYLE, ...style };

  const handle: SimpleTooltipHandle = {
    container: null,

    show(worldX: number, worldY: number, text: string): void {
      handle.hide();

      const tipText = scene.add.text(0, 0, text, {
        fontSize: s.fontSize,
        color: s.fontColor,
        fontFamily: 'monospace',
        lineSpacing: s.lineSpacing,
        wordWrap: { width: s.maxWidth },
      });
      const tw = tipText.width + s.padding * 2;
      const th = tipText.height + s.padding * 2;
      tipText.setPosition(s.padding, s.padding);

      const bg = scene.add.graphics();
      bg.fillStyle(s.bgColor, s.bgAlpha);
      bg.fillRoundedRect(0, 0, tw, th, s.borderRadius);
      bg.lineStyle(1, s.strokeColor, s.strokeAlpha);
      bg.strokeRoundedRect(0, 0, tw, th, s.borderRadius);

      handle.container = scene.add.container(0, 0, [bg, tipText]).setDepth(s.depth);

      // Position: prefer left of cursor, clamp to screen
      let tx = worldX - tw - 10;
      if (tx < 0) tx = worldX + 16;
      let ty = worldY - th / 2;
      ty = Phaser.Math.Clamp(ty, 4, CANVAS_HEIGHT - th - 4);
      tx = Phaser.Math.Clamp(tx, 4, CANVAS_WIDTH - tw - 4);
      handle.container.setPosition(tx, ty);
    },

    hide(): void {
      if (handle.container) {
        handle.container.destroy();
        handle.container = null;
      }
    },
  };

  return handle;
}

// ── Persistent Tooltip ───────────────────────────────────────────────────

export interface PersistentTooltipHandle {
  /** Show the tooltip at the given anchor position with the given text. */
  show(anchorX: number, anchorY: number, text: string): void;
  /** Hide the tooltip (keeps objects alive for reuse). */
  hide(): void;
  /** The container (always exists, toggled via setVisible). */
  container: Phaser.GameObjects.Container;
  /** The text object, for direct manipulation if needed. */
  textObj: Phaser.GameObjects.Text;
  /** The background graphics, for direct manipulation if needed. */
  bgGfx: Phaser.GameObjects.Graphics;
}

/**
 * Creates a persistent tooltip that is shown/hidden without destroy/recreate.
 * The background is redrawn on each show() to fit the new text.
 * Used by scenes that pre-create a tooltip once and reuse it.
 */
export function createPersistentTooltip(
  scene: Phaser.Scene,
  style?: TooltipStyle,
): PersistentTooltipHandle {
  const s = { ...DEFAULT_TOOLTIP_STYLE, ...style };

  const bgGfx = scene.add.graphics();
  const textObj = scene.add.text(0, 0, '', {
    fontSize: s.fontSize,
    color: s.fontColor,
    fontFamily: 'monospace',
    lineSpacing: s.lineSpacing,
    wordWrap: { width: s.maxWidth },
  });
  const container = scene.add.container(0, 0, [bgGfx, textObj])
    .setDepth(s.depth)
    .setVisible(false);

  const handle: PersistentTooltipHandle = {
    container,
    textObj,
    bgGfx,

    show(anchorX: number, anchorY: number, text: string): void {
      textObj.setText(text);
      bgGfx.clear();
      const w = textObj.width + s.padding * 2;
      const h = textObj.height + s.padding * 2;
      bgGfx.fillStyle(s.bgColor, s.bgAlpha);
      bgGfx.fillRoundedRect(-s.padding, -s.padding + 2, w, h, s.borderRadius);
      bgGfx.lineStyle(1, s.strokeColor, s.strokeAlpha);
      bgGfx.strokeRoundedRect(-s.padding, -s.padding + 2, w, h, s.borderRadius);

      // Position with screen clamping
      let tx = anchorX + 14;
      let ty = anchorY + 14;
      if (tx + w > CANVAS_WIDTH - 10) tx = anchorX - w;
      if (ty + h > CANVAS_HEIGHT - 10) ty = anchorY - h;
      container.setPosition(tx, ty).setVisible(true);
    },

    hide(): void {
      container.setVisible(false);
    },
  };

  return handle;
}

// ── Hover effects ────────────────────────────────────────────────────────

/**
 * Adds a hover color change effect to a Phaser Text object.
 * The text must already be interactive (setInteractive called).
 */
export function addHoverColor(
  text: Phaser.GameObjects.Text,
  defaultColor: string,
  hoverColor: string,
): void {
  text.on('pointerover', () => text.setColor(hoverColor));
  text.on('pointerout', () => text.setColor(defaultColor));
}

/**
 * Adds a hover tint effect to a Phaser Image or Sprite.
 * The object must already be interactive (setInteractive called).
 */
export function addHoverTint(
  obj: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  hoverTint: number,
): void {
  obj.on('pointerover', () => obj.setTint(hoverTint));
  obj.on('pointerout', () => obj.clearTint());
}
